import React from "react";
import divorceCostBoxStyle from "./divorceCostBoxStyle";
import { useLocation, useNavigate } from "react-router";
import { Button, Divider } from "@mui/material";
import Grid from "@mui/material/Grid";
import DialogModal from "../../../DialogModal/DialogModal";
import BankDataContainer from "../BankDataContainer";
import FeesService from "../../../../services/FeesService";
import CostCalculator from "../../../../services/divorce/CostCalculator";
import { modalview, pageview } from "../../../../services/useTracker";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";

type DivorceRealCostsBoxProps = {
  divorce: AbstractCase;
};

const DivorceRealCostsBox = ({ divorce }: DivorceRealCostsBoxProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  if (!divorce) {
    return null;
  }

  const processCosts = divorce.processFeeAndExpenses;
  const valueInDispute = CostCalculator.calculateValueInDispute(divorce);
  const installmentRate = (FeesService.getExpenses(true) + CostCalculator.getHearingCost(valueInDispute) + CostCalculator.getProcessCost(valueInDispute)) / 10;
  const formattedInstallmentRate = (Math.round(installmentRate * 100) / 100).toLocaleString("de-DE");
  const hearingSub = divorce.installment
    ? `Sie zahlen die Kosten für Ihren Anwalt in 10 Raten zu je ${formattedInstallmentRate} €.`
    : "Zu zahlen, sobald Ihr Scheidungstermin fest steht";

  const handleCloseBankLayer = () => {
    navigate(-1);
    pageview(location.pathname);
  };

  const handleOpenBankLayer = () => {
    navigate(location.pathname, { state: { bankModalOpen: "costBox" } });
    modalview("Bankdata Modal");
  };

  const costsSum = processCosts + divorce.hearingFee + divorce.courtFee;

  return (
    <Grid container sx={{
      justifyContent: "flex-end"
    }}>
      <Grid sx={divorceCostBoxStyle.costRealLabel} size={8}>
        Verfahrensgebühr
      </Grid>
      <Grid sx={divorceCostBoxStyle.costRealValue} size={4}>
        {processCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      {divorce.paymentType === "private" ? (
        <Grid sx={divorceCostBoxStyle.costRealSub} size={12}>
          <span>Zu zahlen bei Einreichen des Scheidungsantrags</span>
        </Grid>
      ) : (
        [<br key={1} />, <br key={2} />]
      )}
      <Grid sx={divorceCostBoxStyle.costRealLabel} size={8}>
        Terminsgebühr
      </Grid>
      <Grid sx={divorceCostBoxStyle.costRealValue} size={4}>
        {divorce.hearingFee && divorce.hearingFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      {divorce.paymentType !== "processCostSupport"
        ? [
            <Grid key={"1"} sx={divorceCostBoxStyle.costRealSub} size={12}>
              {hearingSub}
            </Grid>,
            <Grid key={"2"} size={12}>
              <Button variant="contained" fullWidth sx={divorceCostBoxStyle.paymentInfoButton} onClick={handleOpenBankLayer}>
                Bankdaten anzeigen
              </Button>
            </Grid>,
          ]
        : [<br key={1} />, <br key={2} />]}
      <Grid sx={divorceCostBoxStyle.costRealLabel} size={8}>
        Gerichtskosten
      </Grid>
      <Grid sx={divorceCostBoxStyle.costRealValue} size={4}>
        {divorce.courtFee
          ? divorce.courtFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
          : divorce.courtFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      {divorce.paymentType !== "processCostSupport" && (
        <Grid sx={divorceCostBoxStyle.costRealSub} size={12}>
          Zu zahlen, sobald Sie die Rechnung vom Gericht erhalten haben
        </Grid>
      )}
      <Grid size={12}>
        <Divider sx={divorceCostBoxStyle.divider} />
      </Grid>
      <Grid
        sx={[divorceCostBoxStyle.costRealLabel, divorceCostBoxStyle.sum]}
        size={8}>
        Scheidungskosten insgesamt
      </Grid>
      <Grid
        sx={[divorceCostBoxStyle.costRealValue, divorceCostBoxStyle.sum]}
        size={4}>
        {costsSum.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
      </Grid>
      {divorce.hasProcessCostSupport && [
        <Grid key={"3"} sx={divorceCostBoxStyle.paymentHint} size={12}>
          Sie müssen voraussichtlich nichts zahlen!
        </Grid>,
        <Grid key={"4"} sx={divorceCostBoxStyle.paymentHintSub} size={12}>
          Wenn Ihr Antrag auf Verfahrenskostenhilfe bewilligt worden ist, sind alle Zahlungen über die Verfahrenskostenhilfe abgedeckt.
        </Grid>,
      ]}
      <DialogModal open={!!(location.state && location.state.bankModalOpen === "costBox")} onClose={handleCloseBankLayer} title={"Bankdaten"}>
        <BankDataContainer
          divorce={divorce}
          hearing={true}
          process={true}
          installmentRate={divorce.installment ? formattedInstallmentRate : null}
        />
      </DialogModal>
    </Grid>
  );
};

export default DivorceRealCostsBox;
